/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

// Chakra Components
import {Box, Flex, Text} from '@chakra-ui/react'

// Project Components
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../item-variant/item-image'
import CartItemVariantName from '../item-variant/item-name'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import CartItemVariantPrice from '../item-variant/item-price'
import LoadingSpinner from '../loading-spinner'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} productLineItem Product line item object.
 * @param {Object|null} [product] Product detail of the product from productLineItem if exists.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {boolean} showLink Should link to the product be displayed
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: cart, order details).
 */
const ProductItem = ({
    loading,
    product,
    productLineItem,
    primaryAction,
    quantity,
    secondaryActions,
    showLink = true,
    showLoading = false,
    variant
}) => {
    const variantProduct = useMemo(
        () => ({
            ...productLineItem,
            ...product,
            price: productLineItem.price ?? product?.price,
            productExists: Boolean(product),
            quantity: quantity ?? productLineItem.quantity
        }),
        [product, productLineItem, quantity]
    )

    return (
        <Box position="relative" data-testid={`sf-cart-item-${productLineItem.productId}`}>
            <ItemVariantProvider variant={variantProduct}>
                {showLoading && <LoadingSpinner />}
                {/* Content */}
                <Flex
                    flexDirection="column"
                    gap={6}
                    backgroundColor="stokkeCore.white"
                    paddingBlockStart={5}
                    paddingInline={4}
                    paddingBlockEnd={4}
                    height="100%"
                >
                    {/* Top Area */}
                    <Flex gap={5} alignItems="flex-start">
                        <CartItemVariantImage
                            imageProps={{
                                loading
                            }}
                            link={showLink && variantProduct['c_product-type'] !== 'Engraving'}
                            variant={variant}
                        />

                        {/* Infos */}
                        <Flex flexDirection="column" alignItems="stretch" gap={3} flex="1 0 0">
                            {/* Name and primary */}
                            <Flex justifyContent="space-between" alignItems="flex-start" gap={6}>
                                <Text as="h3">
                                    <CartItemVariantName
                                        link={
                                            showLink &&
                                            variantProduct['c_product-type'] !== 'Engraving'
                                        }
                                    />
                                </Text>

                                {primaryAction && (
                                    // height=0 so the container doesn't increase in size due to the button
                                    <Box height={0}>{primaryAction}</Box>
                                )}
                            </Flex>

                            {/* Selectors */}
                            <CartItemVariantAttributes includeQuantity />
                        </Flex>
                    </Flex>

                    {/* Bottom area */}
                    {product || secondaryActions ? (
                        <Flex justifyContent="space-between" alignItems="flex-end" gap={4}>
                            {product ? (
                                <CartItemVariantPrice
                                    productLineItem={productLineItem}
                                    variant={variant}
                                />
                            ) : (
                                // Empty div to keep the space
                                <div />
                            )}
                            {secondaryActions}
                        </Flex>
                    ) : null}
                </Flex>
            </ItemVariantProvider>
        </Box>
    )
}

ProductItem.propTypes = {
    loading: PropTypes.oneOf(['lazy', 'eager']),
    primaryAction: PropTypes.node,
    product: PropTypes.object,
    productLineItem: PropTypes.object,
    quantity: PropTypes.number,
    secondaryActions: PropTypes.node,
    showLink: PropTypes.bool,
    showLoading: PropTypes.bool,
    variant: PropTypes.string
}

export default ProductItem
